import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";
import { resolverPropiedadIdPorReferencia } from "../../utils/resolverPropiedadIdPorReferencia";
import { llamarContacto } from "../../utils/gestionVentasContactos";
import {
  aplicarPlaceholdersMensaje,
  nombreParaPlantillaMensaje,
} from "../../utils/nombrePlantillaMensaje";
import FichaResumenPropiedad from "./FichaResumenPropiedad";

const BLOQUES_ORDEN = ["activos", "frios", "eliminados"];
/** Colores sólidos (no pastel): borde + cabecera; título en blanco. */
const BLOQUES_CONFIG = {
  activos:    { label: "Clientes activos", color: "#15803d", headerBg: "#15803d" },
  frios:      { label: "Fríos",            color: "#0891b2", headerBg: "#0891b2" }, // cyan
  eliminados: { label: "Eliminados",       color: "#dc2626", headerBg: "#dc2626" }, // rojo
};

const ACCENT = "#16a34a"; // verde CRM (sustituye lila)

// sel | nombre | tel | email | ref | enviada | visitada | procedencia | agente | agente anterior | alta | etapa
const GRID = "28px minmax(100px,1.2fr) minmax(90px,1fr) minmax(110px,1.1fr) 72px 64px 64px 88px 90px 100px 88px 88px";

/** Cabeceras ordenables (asc → desc → quitar). Acumulables en orden de clic. */
const HEADER_COLS = [
  { label: "", key: null },
  { label: "Nombre", key: null },
  { label: "Teléfono", key: null },
  { label: "Email", key: null },
  { label: "Ref.", key: "propiedad_ref" },
  { label: "Env.", key: null },
  { label: "Vis.", key: null },
  { label: "Procedencia", key: null },
  { label: "Agente", key: "agente_nombre" },
  { label: "Agente anterior", key: "agente_anterior_nombre" },
  { label: "Alta", key: "created_at" },
  { label: "En etapa", key: "etapa_desde_at" },
];

function sortValueForKey(row, key) {
  if (key === "propiedad_ref" || key === "agente_nombre" || key === "agente_anterior_nombre") {
    return String(row?.[key] || "").trim().toLowerCase();
  }
  if (key === "created_at" || key === "etapa_desde_at") {
    const t = row?.[key] ? new Date(row[key]).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

function compareBySortStack(a, b, sortStack) {
  for (const { key, dir } of sortStack) {
    const va = sortValueForKey(a, key);
    const vb = sortValueForKey(b, key);
    const aEmpty = va == null || va === "";
    const bEmpty = vb == null || vb === "";
    if (aEmpty && bEmpty) continue;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else cmp = String(va).localeCompare(String(vb), "es", { sensitivity: "base", numeric: true });
    if (cmp !== 0) return dir === "desc" ? -cmp : cmp;
  }
  return 0;
}

function cycleSortStack(prev, key) {
  const idx = prev.findIndex((s) => s.key === key);
  if (idx < 0) return [...prev, { key, dir: "asc" }];
  const cur = prev[idx];
  if (cur.dir === "asc") {
    const next = prev.slice();
    next[idx] = { key, dir: "desc" };
    return next;
  }
  return prev.filter((_, i) => i !== idx);
}

/** Tonos oficiales CRM (Tareas / ThemeProvider). */
const BTN_WA = "#25D366";
const BTN_EMAIL = "#f97316";
const BTN_LLAMAR = "#ffDE21";

const CFG_CANAL = {
  wa:    { label: "WhatsApp", tag: "WA",    color: BTN_WA,    bg: "rgba(37,211,102,.12)",  border: "rgba(37,211,102,.45)"  },
  email: { label: "Email",    tag: "Email", color: BTN_EMAIL, bg: "rgba(249,115,22,.12)",  border: "rgba(249,115,22,.45)"  },
};

function formatFechaHora(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function rowKeyOf(row) {
  return row.row_key || (row.lead_id ? `lead:${row.lead_id}` : row.cliente_id ? `cli:${row.cliente_id}` : `dem:${row.demanda_id}`);
}

function BadgeWA({ status }) {
  if (!status || status === "verificando") {
    return <span style={{ fontSize: 9, color: "#94a3b8", fontStyle: "italic" }}>WA...</span>;
  }
  if (status === "si") {
    return <span style={{ fontSize: 9, fontWeight: 700, color: "#15803d", background: "rgba(22,163,74,.12)", borderRadius: 8, padding: "1px 5px" }}>WA ok</span>;
  }
  if (status === "no") {
    return <span style={{ fontSize: 9, fontWeight: 700, color: "#dc2626", background: "rgba(220,38,38,.10)", borderRadius: 8, padding: "1px 5px" }}>Sin WA</span>;
  }
  return null;
}

function Checkbox({ checked, indeterminate, onChange, title }) {
  return (
    <div
      role="checkbox" aria-checked={indeterminate ? "mixed" : checked} title={title}
      onClick={onChange}
      style={{
        width: 18, height: 18, borderRadius: 5,
        border: `2px solid ${checked || indeterminate ? ACCENT : "#94a3b8"}`,
        background: checked ? ACCENT : indeterminate ? "rgba(22,163,74,0.35)" : "transparent",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, transition: "all .15s", userSelect: "none",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {checked && <span style={{ color: "#fff", fontSize: 11, lineHeight: 1, fontWeight: 900 }}>v</span>}
      {!checked && indeterminate && <span style={{ color: ACCENT, fontSize: 13, lineHeight: 1, fontWeight: 900 }}>-</span>}
    </div>
  );
}

function FilaContacto({
  row, c, theme, last, seleccionado, onToggle, waStatus,
  navigate, onClose, onAbrirVistaPrevia,
}) {
  const nombre = [row.cliente_nombre, row.cliente_apellido].filter(Boolean).join(" ") || "Sin nombre";
  const telefono = row.cliente_whatsapp || row.cliente_telefono || "";
  const email = row.cliente_email || "";
  const waS = telefono ? (waStatus[telefono] || "verificando") : null;
  const rk = rowKeyOf(row);
  const cellMuted = { fontSize: 11, color: c.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <div style={{
      display: "grid", gridTemplateColumns: GRID, alignItems: "center",
      padding: "8px 12px", gap: 6,
      borderBottom: last ? "none" : `1px solid ${theme.name === "dark" ? c.border : "#e2e2da"}`,
      background: seleccionado
        ? (theme.name === "dark" ? "rgba(22,163,74,0.12)" : "rgba(22,163,74,0.08)")
        : "transparent",
      transition: "background .15s",
    }}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Checkbox checked={seleccionado} onChange={() => onToggle(rk)} title={seleccionado ? "Deseleccionar" : "Seleccionar"} />
      </div>

      <div style={{ minWidth: 0 }}>
        {row.cliente_id ? (
          <button type="button" onClick={() => { onClose(); navigate(`/clientes/${row.cliente_id}`); }}
            style={{ fontWeight: 700, fontSize: 12, color: "#fff", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
            {nombre}
          </button>
        ) : (
          <span style={{ fontWeight: 700, fontSize: 12, color: "#fff" }}>{nombre}</span>
        )}
      </div>

      <div style={{ ...cellMuted, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        {telefono || "—"}
        {waS && <BadgeWA status={waS} />}
      </div>

      <div style={{ ...cellMuted, color: BTN_EMAIL }} title={email || ""}>{email || "—"}</div>

      <div style={{ minWidth: 0 }}>
        {row.propiedad_ref ? (
          <button
            type="button"
            title={`Vista previa ${row.propiedad_ref}`}
            onClick={() => onAbrirVistaPrevia(row.propiedad_ref, row.propiedad_entrada_id)}
            style={{
              fontSize: 11, fontWeight: 700, color: BTN_LLAMAR, background: "none",
              border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit",
              textDecoration: "underline", textUnderlineOffset: 2,
            }}
          >
            {row.propiedad_ref}
          </button>
        ) : (
          <span style={cellMuted}>—</span>
        )}
      </div>

      <div style={{ textAlign: "center" }}>
        {row.enviada ? (
          <span
            title={
              row.enviada_email && !row.enviada_whatsapp
                ? "Enviada por email"
                : "Enviada por WhatsApp"
            }
            style={{
              fontSize: 9, fontWeight: 800, color: "#fff", borderRadius: 5, padding: "2px 6px",
              background: row.enviada_email && !row.enviada_whatsapp ? "#d97706" : "#16a34a",
              textTransform: "uppercase",
            }}
          >
            Sí
          </span>
        ) : (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>
        )}
      </div>

      <div style={{ textAlign: "center" }}>
        {row.visitada ? (
          <span
            title={row.ultima_visita ? `Visita ${formatFechaHora(row.ultima_visita)}` : "Visitada"}
            style={{
              fontSize: 9, fontWeight: 800, color: "#fff", borderRadius: 5, padding: "2px 6px",
              background: "#2563eb", textTransform: "uppercase",
            }}
          >
            Sí
          </span>
        ) : (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>
        )}
      </div>

      <div style={cellMuted} title={row.fuente || ""}>{row.fuente || "—"}</div>
      <div style={{ ...cellMuted, color: ACCENT }} title={row.agente_nombre || ""}>{row.agente_nombre || "—"}</div>
      <div style={{ ...cellMuted, color: ACCENT }} title={row.agente_anterior_nombre || ""}>{row.agente_anterior_nombre || "—"}</div>
      <div style={{ ...cellMuted, fontSize: 10 }}>{formatFechaHora(row.created_at)}</div>
      <div style={{ ...cellMuted, fontSize: 10 }}>{formatFechaHora(row.etapa_desde_at)}</div>
    </div>
  );
}

function BloqueGrupo({
  bloque, contactos, c, theme, seleccionados, waStatus,
  onToggle, onToggleGrupo, navigate, onClose, onAbrirVistaPrevia,
  stickyOffset = 0,
}) {
  const cfg = BLOQUES_CONFIG[bloque] || BLOQUES_CONFIG.activos;
  const [collapsed, setCollapsed] = useState(false);
  /** [{ key, dir: 'asc'|'desc' }, ...] en orden de aplicación (primero = primario). */
  const [sortStack, setSortStack] = useState([]);
  const ids = contactos.map(rowKeyOf);
  const todosGrupo = ids.length > 0 && ids.every((id) => seleccionados.has(id));
  const algunoGrupo = ids.some((id) => seleccionados.has(id));

  const contactosOrdenados = useMemo(() => {
    if (!sortStack.length) return contactos;
    return contactos.slice().sort((a, b) => compareBySortStack(a, b, sortStack));
  }, [contactos, sortStack]);

  return (
    <div style={{
      marginBottom: 16,
      border: `2px solid ${cfg.color}`,
      borderRadius: 10,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        background: cfg.headerBg,
        padding: stickyOffset === 0 ? "12px 16px 10px" : "10px 12px",
        position: "sticky",
        top: stickyOffset,
        zIndex: 4,
        borderRadius: stickyOffset === 0 ? 0 : "8px 8px 0 0",
        marginLeft: stickyOffset === 0 ? -16 : 0,
        marginRight: stickyOffset === 0 ? -16 : 0,
      }}>
        <div
          role="checkbox"
          aria-checked={todosGrupo ? true : algunoGrupo ? "mixed" : false}
          title={todosGrupo ? "Deseleccionar grupo" : "Seleccionar grupo"}
          onClick={() => onToggleGrupo(ids, todosGrupo)}
          style={{
            width: 18, height: 18, borderRadius: 5,
            border: "2px solid #fff",
            background: todosGrupo ? "#fff" : algunoGrupo ? "rgba(255,255,255,0.45)" : "transparent",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {todosGrupo && <span style={{ color: cfg.headerBg, fontSize: 11, lineHeight: 1, fontWeight: 900 }}>v</span>}
          {!todosGrupo && algunoGrupo && <span style={{ color: "#fff", fontSize: 13, lineHeight: 1, fontWeight: 900 }}>-</span>}
        </div>
        <button type="button" onClick={() => setCollapsed(!collapsed)}
          style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
          <span style={{ fontWeight: 800, fontSize: 14, color: "#fff", flex: 1, textAlign: "left", letterSpacing: "0.02em" }}>{cfg.label}</span>
          <span style={{ fontSize: 11, fontWeight: 800, color: cfg.headerBg, background: "#fff", borderRadius: 20, padding: "2px 9px" }}>{contactos.length}</span>
          <span style={{ fontSize: 14, color: "#fff", marginLeft: 2, fontWeight: 700 }}>{collapsed ? "+" : "−"}</span>
        </button>
      </div>
      {!collapsed && (
        <div style={{ background: theme.name === "dark" ? "#0f172a" : "#f8f8f2" }}>
          <div style={{
            display: "grid", gridTemplateColumns: GRID, padding: "6px 12px", gap: 6,
            background: theme.name === "dark" ? "#0f172a" : "#e2e2da",
            borderBottom: `1px solid ${theme.name === "dark" ? c.border : "#d0d0c8"}`,
            minWidth: 1000,
            position: "sticky",
            top: stickyOffset + 42,
            zIndex: 3,
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          }}>
            {HEADER_COLS.map((col, i) => {
              const sortEntry = col.key ? sortStack.find((s) => s.key === col.key) : null;
              const isActive = !!sortEntry;
              const centered = i === 0 || i === 5 || i === 6 || col.key === "created_at" || col.key === "etapa_desde_at";
              return (
                <span
                  key={col.label || `h${i}`}
                  role={col.key ? "button" : undefined}
                  tabIndex={col.key ? 0 : undefined}
                  onClick={col.key ? () => setSortStack((prev) => cycleSortStack(prev, col.key)) : undefined}
                  onKeyDown={col.key ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSortStack((prev) => cycleSortStack(prev, col.key));
                    }
                  } : undefined}
                  onMouseEnter={col.key ? (e) => {
                    const arrow = e.currentTarget.querySelector("[data-sort-arrow]");
                    if (arrow && !isActive) arrow.style.opacity = "1";
                  } : undefined}
                  onMouseLeave={col.key ? (e) => {
                    const arrow = e.currentTarget.querySelector("[data-sort-arrow]");
                    if (arrow && !isActive) arrow.style.opacity = "0";
                  } : undefined}
                  style={{
                    fontSize: 9, fontWeight: 700, color: c.textMuted,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                    textAlign: centered ? "center" : "left",
                    cursor: col.key ? "pointer" : "default",
                    userSelect: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    justifyContent: centered ? "center" : "flex-start",
                  }}
                >
                  {col.label}
                  {col.key ? (
                    <span
                      data-sort-arrow=""
                      style={{
                        opacity: isActive ? 1 : 0,
                        transition: "opacity 0.15s",
                        fontSize: 10,
                        color: "#fff",
                        textShadow: "0 0 2px rgba(0,0,0,0.75), 0 1px 2px rgba(0,0,0,0.55)",
                        lineHeight: 1,
                      }}
                    >
                      {isActive ? (sortEntry.dir === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  ) : null}
                </span>
              );
            })}
          </div>
          <div style={{ minWidth: 1000 }}>
            {contactosOrdenados.map((row, idx) => (
              <FilaContacto
                key={rowKeyOf(row)}
                row={row} c={c} theme={theme}
                last={idx === contactosOrdenados.length - 1}
                seleccionado={seleccionados.has(rowKeyOf(row))}
                onToggle={onToggle}
                waStatus={waStatus}
                navigate={navigate} onClose={onClose}
                onAbrirVistaPrevia={onAbrirVistaPrevia}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function planificarEnvios(canal, contactos, waStatus = {}) {
  return contactos.map((f) => {
    const tel = f.cliente_whatsapp || f.cliente_telefono || "";
    const email = f.cliente_email || "";
    const nombre = [f.cliente_nombre, f.cliente_apellido].filter(Boolean).join(" ") || "Sin nombre";
    const waOk = tel && waStatus[tel] !== "no";

    if (canal === "wa") {
      if (waOk) return { f, nombre, canalReal: "wa", dest: tel, subsidiario: false };
      if (email) return { f, nombre, canalReal: "email", dest: email, subsidiario: true };
      return { f, nombre, canalReal: null, dest: null, subsidiario: false };
    }
    if (email) return { f, nombre, canalReal: "email", dest: email, subsidiario: false };
    if (waOk) return { f, nombre, canalReal: "wa", dest: tel, subsidiario: true };
    return { f, nombre, canalReal: null, dest: null, subsidiario: false };
  });
}

function PanelBulkSend({ canal, contactos, propiedadId, propiedadLabel, waStatus, c, theme, onClosePanel, onCloseAll }) {
  const plan = planificarEnvios(canal, contactos, waStatus);
  const conEnvio = plan.filter((p) => p.canalReal !== null);
  const sinEnvio = plan.filter((p) => p.canalReal === null);
  const subsidiarios = conEnvio.filter((p) => p.subsidiario);
  const pendientes = Object.values(waStatus).filter((v) => v === "verificando").length;

  const cfg = CFG_CANAL[canal];
  const asuntoEmail = `Propiedad de tu interes${propiedadLabel ? " - Ref. " + propiedadLabel : ""}`;
  const plantilla = canal === "wa"
    ? `Hola {nombre}, te contacto desde Vitalcasa con una propiedad que creo puede interesarte${propiedadLabel ? ` (Ref. ${propiedadLabel})` : ""}. Tienes unos minutos para hablar?`
    : `Hola {nombre},\n\nTe escribo desde Vitalcasa para informarte sobre una propiedad que encaja con tus criterios de busqueda${propiedadLabel ? ` (Ref. ${propiedadLabel})` : ""}.\n\nQuedo a tu disposicion para cualquier consulta.\n\nSaludos,\nEquipo Vitalcasa`;

  const [mensaje, setMensaje] = useState(plantilla);
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [done, setDone] = useState(false);

  const enviar = async () => {
    setEnviando(true);
    const results = [];
    for (const item of conEnvio) {
      const { f, nombre, canalReal, dest, subsidiario } = item;
      const nombreCorto = nombreParaPlantillaMensaje(nombre);
      const refProp = f.propiedad_match_ref || propiedadLabel || "";
      const propIdEnvio = f.propiedad_match_id || propiedadId;
      const msgPersonal = aplicarPlaceholdersMensaje(mensaje, nombreCorto, "")
        .replace(/\{ref\}/gi, refProp || "");
      const asuntoItem = `Propiedad de tu interes${refProp ? " - Ref. " + refProp : ""}`;
      let ok = false; let error = null;
      try {
        if (canalReal === "wa") {
          await api.post("/whatsapp/enviar", {
            telefono: dest, mensaje: msgPersonal,
            lead_id: f.lead_id || null, cliente_id: f.cliente_id || null,
            propiedades_ids: propIdEnvio ? [propIdEnvio] : [],
          });
        } else {
          await api.post("/email/enviar", {
            destinatario: dest, asunto: asuntoItem, mensaje: msgPersonal,
            lead_id: f.lead_id || null, cliente_id: f.cliente_id || null,
            propiedades_ids: propIdEnvio ? [propIdEnvio] : [],
          });
        }
        ok = true;
      } catch (e) {
        error = e.response?.data?.error || e.message;
      }
      results.push({ id: rowKeyOf(f), nombre, canalReal, subsidiario, dest, ok, error });
      setResultados([...results]);
      await new Promise((r) => setTimeout(r, 350));
    }
    setEnviando(false);
    setDone(true);
  };

  const nEnviados = resultados.filter((r) => r.ok).length;
  const nFallidos = resultados.filter((r) => !r.ok).length;
  const nWA = resultados.filter((r) => r.ok && r.canalReal === "wa").length;
  const nEmail = resultados.filter((r) => r.ok && r.canalReal === "email").length;
  const nSubOk = resultados.filter((r) => r.ok && r.subsidiario).length;

  const ChipDest = ({ item }) => {
    const res = resultados.find((r) => r.id === rowKeyOf(item.f));
    const cfgR = CFG_CANAL[item.canalReal] || CFG_CANAL.wa;
    const pendiente = !res && enviando;
    const sinEnviarAun = !res && !enviando;
    // Chip sólido del canal + texto blanco (el #1e293b anterior desaparecía en dark).
    let border = cfgR.color;
    let bg = cfgR.color;
    let color = "#ffffff";
    let icono = "?";
    if (res?.ok) { border = "#15803d"; bg = "#15803d"; color = "#fff"; icono = "OK"; }
    if (res && !res.ok) { border = "#dc2626"; bg = "#dc2626"; color = "#fff"; icono = "X"; }
    if (pendiente) icono = "...";
    if (sinEnviarAun) icono = "·";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, border: `1px solid ${border}`, background: bg, color, transition: "all .2s" }}>
        <span style={{ fontWeight: 700, minWidth: 14, textAlign: "center", fontSize: 10, opacity: 0.9 }}>{icono}</span>
        <strong style={{ color: "#fff", fontWeight: 700 }}>{item.nombre.split(" ")[0]}</strong>
        <span style={{ opacity: 0.85, fontSize: 10, color: "#fff" }}>{cfgR.tag}</span>
        {item.subsidiario && <span style={{ fontSize: 9, fontWeight: 700, background: "rgba(0,0,0,.2)", borderRadius: 10, padding: "1px 5px", color: "#fde68a" }}>sub</span>}
      </div>
    );
  };

  if (done) {
    return (
      <div style={{ position: "absolute", inset: 0, background: theme.name === "dark" ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: 16, padding: 20 }}>
        <div style={{ width: "100%", maxWidth: 520, background: theme.name === "dark" ? c.bgCard : "#f8f8f2", borderRadius: 14, border: `2px solid ${nFallidos === 0 ? "rgba(22,163,74,.5)" : "rgba(239,68,68,.4)"}`, padding: "22px 24px", maxHeight: "80vh", overflowY: "auto" }}>
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 38, marginBottom: 6 }}>{nFallidos === 0 ? "OK" : nEnviados > 0 ? "PARCIAL" : "ERROR"}</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: nFallidos === 0 ? "#15803d" : nEnviados > 0 ? "#f59e0b" : "#dc2626" }}>
              {nFallidos === 0 ? "Envio completado" : nEnviados > 0 ? "Envio parcialmente completado" : "Envio fallido"}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 10, marginBottom: 18 }}>
            {nEnviados > 0 && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(22,163,74,.10)", border: "1.5px solid rgba(22,163,74,.3)", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#15803d" }}>{nEnviados}</div>
                <div style={{ fontSize: 11, color: "#15803d", fontWeight: 600 }}>Enviado{nEnviados !== 1 ? "s" : ""}</div>
              </div>
            )}
            {nWA > 0 && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(22,163,74,.06)", border: "1px solid rgba(22,163,74,.2)", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#16a34a" }}>{nWA}</div>
                <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>por WhatsApp</div>
              </div>
            )}
            {nEmail > 0 && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(14,165,233,.07)", border: "1px solid rgba(14,165,233,.25)", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#0ea5e9" }}>{nEmail}</div>
                <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 600 }}>por Email</div>
              </div>
            )}
            {nSubOk > 0 && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.3)", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#f59e0b" }}>{nSubOk}</div>
                <div style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>subsidiario</div>
              </div>
            )}
            {nFallidos > 0 && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(239,68,68,.08)", border: "1.5px solid rgba(239,68,68,.3)", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#dc2626" }}>{nFallidos}</div>
                <div style={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>Fallido{nFallidos !== 1 ? "s" : ""}</div>
              </div>
            )}
            {sinEnvio.length > 0 && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(100,116,139,.07)", border: "1px solid rgba(100,116,139,.25)", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#64748b" }}>{sinEnvio.length}</div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Saltado{sinEnvio.length !== 1 ? "s" : ""}</div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 18 }}>
            {resultados.map((r, i) => {
              const cfgR = CFG_CANAL[r.canalReal] || CFG_CANAL.wa;
              return (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, padding: "7px 12px", borderRadius: 8, background: r.ok ? "rgba(22,163,74,.06)" : "rgba(239,68,68,.06)", border: `1px solid ${r.ok ? "rgba(22,163,74,.2)" : "rgba(239,68,68,.2)"}` }}>
                  <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", background: r.ok ? "#16a34a" : "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, fontFamily: "Arial,sans-serif" }}>
                    {r.ok ? "SI" : "X"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700 }}>{r.nombre}</span>
                    <span style={{ color: "#475569", marginLeft: 8, fontWeight: 600, fontSize: 11 }}>
                      [{cfgR.tag}{r.subsidiario ? " sub." : ""}]
                    </span>
                    <span style={{ color: "#94a3b8", marginLeft: 6, fontSize: 11 }}>{r.dest}</span>
                    {!r.ok && <div style={{ color: "#dc2626", marginTop: 3, fontSize: 11 }}>{r.error}</div>}
                  </div>
                </div>
              );
            })}
            {sinEnvio.map((item, i) => (
              <div key={"skip" + i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, padding: "7px 12px", borderRadius: 8, background: "rgba(100,116,139,.05)", border: "1px solid rgba(100,116,139,.15)", color: "#94a3b8" }}>
                <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", background: "#94a3b8", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, fontFamily: "Arial,sans-serif" }}>-</div>
                <span><strong>{item.nombre}</strong> — sin telefono ni email, no enviado</span>
              </div>
            ))}
          </div>
          <button type="button" onClick={onCloseAll}
            style={{ width: "100%", padding: "11px 0", borderRadius: 10, border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Cerrar y volver a propiedades
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "absolute", inset: 0, background: theme.name === "dark" ? "rgba(0,0,0,0.70)" : "rgba(0,0,0,0.50)", display: "flex", alignItems: "flex-end", zIndex: 10, borderRadius: 16 }}>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", background: theme.name === "dark" ? c.bgCard : "#f0f0e8", borderRadius: "0 0 16px 16px", borderTop: `3px solid ${cfg.color}`, maxHeight: "92%" }}>
        <div style={{ padding: "14px 20px 10px", borderBottom: `1px solid ${theme.name === "dark" ? c.border : "#d8d8d0"}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, color: "#1e293b" }}>
                Enviar por <span style={{ color: cfg.color }}>{cfg.label}</span>
              </h3>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "#64748b" }}>
                {conEnvio.length} de {contactos.length} contactos recibiran el mensaje
                {subsidiarios.length > 0 && <span style={{ color: "#f59e0b", fontWeight: 600 }}> — {subsidiarios.length} por canal subsidiario</span>}
              </p>
              {pendientes > 0 && (
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                  Verificando WA en segundo plano ({pendientes} pendientes)
                </p>
              )}
            </div>
            {!enviando && <button type="button" onClick={onClosePanel} style={{ border: "none", background: "transparent", fontSize: 18, color: "#94a3b8", cursor: "pointer", flexShrink: 0 }}>X</button>}
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "14px 20px 20px" }}>
          {subsidiarios.length > 0 && (
            <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(245,158,11,.09)", border: "1px solid rgba(245,158,11,.35)" }}>
              <div style={{ fontSize: 11, color: "#92400e", fontWeight: 700, marginBottom: 2 }}>Envio subsidiario activado</div>
              <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                {subsidiarios.length === 1
                  ? `${subsidiarios[0].nombre.split(" ")[0]} no tiene ${canal === "wa" ? "WA confirmado" : "email"} -- se enviara por ${CFG_CANAL[subsidiarios[0].canalReal]?.label}.`
                  : `${subsidiarios.length} contactos sin ${canal === "wa" ? "WA confirmado" : "email"} recibiran el mensaje por canal subsidiario.`}
              </div>
            </div>
          )}

          {conEnvio.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                Destinatarios ({conEnvio.length})
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {conEnvio.map((item) => <ChipDest key={rowKeyOf(item.f)} item={item} />)}
              </div>
            </div>
          )}

          {sinEnvio.length > 0 && (
            <div style={{ marginBottom: 14, padding: "6px 10px", borderRadius: 8, background: "rgba(100,116,139,.07)", border: "1px solid rgba(100,116,139,.25)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", marginBottom: 4 }}>Sin telefono ni email -- no se pueden enviar ({sinEnvio.length})</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {sinEnvio.map((item) => (
                  <span key={rowKeyOf(item.f)} style={{ fontSize: 10, color: "#94a3b8", padding: "2px 8px", borderRadius: 20, border: "1px solid rgba(148,163,184,.3)" }}>
                    {item.nombre.split(" ")[0]}
                  </span>
                ))}
              </div>
            </div>
          )}

          {conEnvio.length === 0 && (
            <div style={{ padding: "10px 14px", borderRadius: 9, background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.3)", marginBottom: 14 }}>
              <p style={{ margin: 0, color: "#dc2626", fontSize: 13, fontWeight: 600 }}>Ningun contacto tiene telefono ni email registrado.</p>
            </div>
          )}

          {conEnvio.length > 0 && (
            <>
              <div style={{ borderTop: `1px solid ${theme.name === "dark" ? c.border : "#d0d0c8"}`, marginBottom: 14 }} />
              <textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)} disabled={enviando}
                rows={canal === "wa" ? 4 : 5} placeholder="Escribe el mensaje..."
                style={{ width: "100%", borderRadius: 8, border: `1px solid ${theme.name === "dark" ? c.border : "#c0c0b8"}`, background: theme.name === "dark" ? c.bgInput : "#fff", color: c.textPrimary, fontSize: 12, padding: "8px 10px", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", opacity: enviando ? 0.6 : 1, marginBottom: 6 }} />
              <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 14 }}>
                Usa <code style={{ background: "rgba(0,0,0,0.08)", padding: "1px 4px", borderRadius: 3 }}>{"{nombre}"}</code> para personalizar. Queda registrado en historial con ref., fecha y canal.
              </div>
              <button type="button" onClick={enviar} disabled={enviando || !mensaje.trim()}
                style={{ width: "100%", padding: "13px 0", borderRadius: 10, border: "none", background: enviando ? "#94a3b8" : cfg.color, color: "#fff", fontWeight: 700, fontSize: 14, cursor: enviando ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {enviando
                  ? `Enviando... (${resultados.length}/${conEnvio.length})`
                  : `Enviar a ${conEnvio.length} contacto${conEnvio.length !== 1 ? "s" : ""} por ${cfg.label}`}
              </button>
            </>
          )}

          {conEnvio.length === 0 && (
            <button type="button" onClick={onClosePanel}
              style={{ width: "100%", padding: "10px 0", borderRadius: 9, border: `1px solid ${c.border}`, background: "transparent", color: "#94a3b8", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const PROP_HEADER_BG = "#ea580c"; // naranja — agrupación por propiedad en cruce
/** Altura aproximada cabecera Ref. sticky (padding 12+12 + línea ~20). */
const PROP_STICKY_H = 44;

function BloquePropiedadCruce({
  grupo, c, theme, seleccionados, waStatus,
  onToggle, onToggleGrupo, navigate, onClose, onAbrirVistaPrevia,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const porBloque = { activos: [], frios: [], eliminados: [] };
  for (const f of grupo.contactos || []) {
    const b = f.bloque && porBloque[f.bloque] ? f.bloque : "activos";
    porBloque[b].push(f);
  }
  const bloquesConDatos = BLOQUES_ORDEN.filter((b) => porBloque[b]?.length > 0);
  const n = (grupo.contactos || []).length;

  return (
    <div style={{
      marginBottom: 20,
      border: `2px solid ${PROP_HEADER_BG}`,
      borderRadius: 12,
    }}>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          background: PROP_HEADER_BG,
          padding: "12px 16px",
          position: "sticky",
          top: 0,
          zIndex: 5,
          marginLeft: -16,
          marginRight: -16,
          boxSizing: "border-box",
          width: "calc(100% + 32px)",
        }}
      >
        <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
          {grupo.referencia ? (
            <button
              type="button"
              title={`Vista previa ${grupo.referencia}`}
              onClick={(e) => {
                e.stopPropagation();
                onAbrirVistaPrevia?.(grupo.referencia, grupo.propiedad_id);
              }}
              style={{
                fontWeight: 800, fontSize: 14, color: "#fff", background: "none",
                border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit",
                textDecoration: "underline", textUnderlineOffset: 3,
              }}
            >
              Ref. {grupo.referencia}
            </button>
          ) : (
            <span style={{ fontWeight: 800, fontSize: 14, color: "#fff" }}>
              {grupo.titulo || "Propiedad"}
            </span>
          )}
          {grupo.referencia && grupo.titulo ? (
            <span style={{ fontWeight: 600, fontSize: 13, color: "rgba(255,255,255,0.9)", marginLeft: 8 }}>
              — {grupo.titulo}
            </span>
          ) : null}
        </div>
        <span style={{
          fontSize: 11, fontWeight: 800, color: PROP_HEADER_BG, background: "#fff",
          borderRadius: 20, padding: "2px 10px", flexShrink: 0,
        }}>
          {n} coincidencia{n !== 1 ? "s" : ""}
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          style={{
            border: "none", background: "transparent", color: "#fff",
            fontSize: 16, fontWeight: 700, cursor: "pointer", padding: "0 4px",
            fontFamily: "inherit", flexShrink: 0,
          }}
          title={collapsed ? "Expandir" : "Contraer"}
        >
          {collapsed ? "+" : "−"}
        </button>
      </div>
      {!collapsed && (
        <div style={{ padding: "8px 12px 4px", background: theme.name === "dark" ? "#0f172a" : "#f8f8f2" }}>
          {bloquesConDatos.map((bloque) => (
            <BloqueGrupo
              key={bloque}
              bloque={bloque}
              contactos={porBloque[bloque]}
              c={c} theme={theme}
              seleccionados={seleccionados}
              waStatus={waStatus}
              onToggle={onToggle}
              onToggleGrupo={onToggleGrupo}
              navigate={navigate}
              onClose={onClose}
              onAbrirVistaPrevia={onAbrirVistaPrevia}
              stickyOffset={PROP_STICKY_H}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ModalContactosInteresados({
  isOpen, onClose, propiedadId, propiedadLabel, onEnviarWA, onEnviarEmail, modoCruce = false,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [filas, setFilas] = useState([]);
  const [gruposCruce, setGruposCruce] = useState([]);
  const [totalesCruce, setTotalesCruce] = useState({ props: 0, coin: 0 });
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [panelBulk, setPanelBulk] = useState(null);
  const [waStatus, setWaStatus] = useState({});
  const [vistaPreviaPropId, setVistaPreviaPropId] = useState(null);
  const [llamando, setLlamando] = useState(false);
  const [msgLlamada, setMsgLlamada] = useState("");
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();

  useEffect(() => {
    if (!isOpen) return;
    if (!modoCruce && !propiedadId) return;
    let cancel = false;
    setSeleccionados(new Set());
    setPanelBulk(null);
    setWaStatus({});
    setVistaPreviaPropId(null);
    setMsgLlamada("");
    setGruposCruce([]);
    setFilas([]);
    (async () => {
      setCargando(true); setError("");
      try {
        if (modoCruce) {
          const res = await api.get("/propiedades/cruce-interesados");
          if (cancel) return;
          const grupos = res.data.data || [];
          setGruposCruce(grupos);
          setTotalesCruce({
            props: res.data.total_propiedades ?? grupos.length,
            coin: res.data.total_coincidencias ?? grupos.reduce((a, g) => a + (g.contactos?.length || 0), 0),
          });
          setFilas(grupos.flatMap((g) => g.contactos || []));
        } else {
          const res = await api.get(`/propiedades/${propiedadId}/coincidencias-demandas`);
          if (cancel) return;
          setFilas(res.data.data || []);
        }
      } catch (e) {
        if (!cancel) setError(e.response?.data?.error || "No se pudieron cargar las coincidencias");
      } finally {
        if (!cancel) setCargando(false);
      }
    })();
    return () => { cancel = true; };
  }, [isOpen, propiedadId, modoCruce]);

  useEffect(() => {
    if (filas.length === 0) return;
    const telefonos = [...new Set(filas.map((f) => f.cliente_whatsapp || f.cliente_telefono).filter(Boolean))];
    if (telefonos.length === 0) return;

    const initial = {};
    telefonos.forEach((t) => { initial[t] = "verificando"; });
    setWaStatus(initial);

    let cancelled = false;
    (async () => {
      for (const tel of telefonos) {
        if (cancelled) break;
        try {
          const res = await api.post("/whatsapp/verificar-numero", { telefono: tel });
          if (!cancelled) {
            const estado = res.data.tieneWA === true ? "si" : res.data.tieneWA === false ? "no" : (res.data.motivo || "error");
            setWaStatus((prev) => ({ ...prev, [tel]: estado }));
          }
        } catch {
          if (!cancelled) setWaStatus((prev) => ({ ...prev, [tel]: "error" }));
        }
        await new Promise((r) => setTimeout(r, 600));
      }
    })();
    return () => { cancelled = true; };
  }, [filas]);

  const todosIds = filas.map(rowKeyOf);
  const todosSeleccionados = todosIds.length > 0 && todosIds.every((id) => seleccionados.has(id));
  const algunoSeleccionado = todosIds.some((id) => seleccionados.has(id));
  const nSeleccionados = seleccionados.size;

  const toggleSeleccionado = useCallback((rk) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(rk)) next.delete(rk); else next.add(rk);
      return next;
    });
  }, []);

  const toggleTodos = () => setSeleccionados(todosSeleccionados ? new Set() : new Set(todosIds));

  const toggleGrupo = (ids, todosActivos) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (todosActivos) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const porBloque = { activos: [], frios: [], eliminados: [] };
  for (const f of filas) {
    const b = f.bloque && porBloque[f.bloque] ? f.bloque : "activos";
    porBloque[b].push(f);
  }
  const bloquesConDatos = BLOQUES_ORDEN.filter((b) => porBloque[b]?.length > 0);
  const contactosSeleccionados = filas.filter((f) => seleccionados.has(rowKeyOf(f)));
  const waPendientes = Object.values(waStatus).filter((v) => v === "verificando").length;

  const abrirVistaPrevia = async (ref, propIdDirecto) => {
    if (propIdDirecto) {
      setVistaPreviaPropId(propIdDirecto);
      return;
    }
    const propId = await resolverPropiedadIdPorReferencia(ref);
    if (propId) setVistaPreviaPropId(propId);
  };

  const handleLlamar = async () => {
    if (contactosSeleccionados.length !== 1) return;
    const row = contactosSeleccionados[0];
    const telefono = row.cliente_whatsapp || row.cliente_telefono || "";
    if (!telefono) {
      setMsgLlamada("El contacto no tiene teléfono.");
      return;
    }
    setLlamando(true);
    setMsgLlamada("");
    try {
      await llamarContacto(
        { telefono, cliente_id: row.cliente_id, lead_id: row.lead_id },
        { clienteId: row.cliente_id, leadId: row.lead_id }
      );
      setMsgLlamada("Llamada iniciada.");
    } catch (e) {
      setMsgLlamada(e.response?.data?.error || e.message || "No se pudo llamar");
    } finally {
      setLlamando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}>
      <div ref={dragRef} onClick={(e) => e.stopPropagation()}
        style={{
          width: 1180, maxWidth: "100%", height: "94vh", display: "flex", flexDirection: "column",
          background: theme.name === "dark" ? c.bgCard : "#c8c8be", borderRadius: 16,
          border: `1px solid ${theme.name === "dark" ? c.border : "#a8a8a0"}`,
          boxShadow: theme.name === "dark" ? "none" : "0 16px 48px rgba(0,0,0,.30), 0 4px 12px rgba(0,0,0,.15)",
          position: "relative", ...panelPositionStyle,
        }}>

        <div onMouseDown={startDrag}
          style={{
            padding: "13px 20px", borderBottom: `1px solid ${theme.name === "dark" ? c.border : "#a8a8a0"}`,
            background: theme.name === "dark" ? (c.bgCard || "#181c24") : "#e0e0d8",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            cursor: "grab", userSelect: "none", flexShrink: 0,
          }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, color: c.textPrimary }}>
              {modoCruce ? "Cruce clientes" : "Contactos interesados"}
            </h2>
            <p style={{ margin: "3px 0 0", fontSize: 11, color: c.textMuted }}>
              {modoCruce
                ? "Propiedades con interesados — por propiedad, luego Activos / Fríos / Eliminados"
                : "Demandas activas y leads con propiedad de entrada compatible — Activos / Fríos / Eliminados"}
              {waPendientes > 0 && <span style={{ color: "#94a3b8", fontStyle: "italic" }}> — verificando WA ({waPendientes})</span>}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!cargando && (modoCruce ? totalesCruce.coin > 0 : filas.length > 0) && (
              <span style={{
                fontSize: 11, fontWeight: 700, color: c.textMuted,
                background: theme.name === "dark" ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.08)",
                borderRadius: 20, padding: "2px 10px",
              }}>
                {modoCruce
                  ? `${totalesCruce.props} propiedad${totalesCruce.props !== 1 ? "es" : ""} con ${totalesCruce.coin} coincidencia${totalesCruce.coin !== 1 ? "s" : ""} totales`
                  : `${filas.length} coincidencia${filas.length !== 1 ? "s" : ""}`}
              </span>
            )}
            <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 20, color: c.textMuted, cursor: "pointer" }}>X</button>
          </div>
        </div>

        {!cargando && filas.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 16px",
            borderBottom: `1px solid ${theme.name === "dark" ? c.border : "#d0d0c8"}`,
            background: theme.name === "dark" ? (c.bgCard || "#181c24") : "#e8e8e0",
            flexShrink: 0,
          }}>
            <Checkbox
              checked={todosSeleccionados}
              indeterminate={!todosSeleccionados && algunoSeleccionado}
              onChange={toggleTodos}
              title={todosSeleccionados ? "Deseleccionar todos" : "Seleccionar todos"}
            />
            <span style={{ fontSize: 12, color: c.textMuted, cursor: "pointer", userSelect: "none" }} onClick={toggleTodos}>
              {todosSeleccionados ? "Deseleccionar todos" : "Seleccionar todos"}
            </span>
            {nSeleccionados > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginLeft: 4 }}>
                — {nSeleccionados} seleccionado{nSeleccionados !== 1 ? "s" : ""}
              </span>
            )}
            {msgLlamada && (
              <span style={{ fontSize: 11, color: msgLlamada.includes("iniciada") ? "#15803d" : "#dc2626", marginLeft: 8 }}>
                {msgLlamada}
              </span>
            )}
          </div>
        )}

        {/* Sin padding-top: evita franja transparente sobre cabeceras sticky. */}
        <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
          {cargando && (
            <p style={{ color: c.textMuted, fontSize: 13 }}>
              {modoCruce ? "Cruzando propiedades con clientes…" : "Buscando coincidencias..."}
            </p>
          )}
          {error && <p style={{ color: "#ef4444", fontSize: 13 }}>{error}</p>}
          {!cargando && !error && filas.length === 0 && (
            <p style={{ color: c.textMuted, fontSize: 13 }}>
              {modoCruce
                ? "Ninguna propiedad tiene contactos interesados que coincidan."
                : "No hay contactos que coincidan (demandas ni leads con propiedad de entrada)."}
            </p>
          )}
          {!cargando && !error && modoCruce && gruposCruce.map((g) => (
            <BloquePropiedadCruce
              key={g.propiedad_id}
              grupo={g}
              c={c} theme={theme}
              seleccionados={seleccionados}
              waStatus={waStatus}
              onToggle={toggleSeleccionado}
              onToggleGrupo={toggleGrupo}
              navigate={navigate}
              onClose={onClose}
              onAbrirVistaPrevia={abrirVistaPrevia}
            />
          ))}
          {!cargando && !error && !modoCruce && bloquesConDatos.map((bloque) => (
            <BloqueGrupo
              key={bloque}
              bloque={bloque}
              contactos={porBloque[bloque]}
              c={c} theme={theme}
              seleccionados={seleccionados}
              waStatus={waStatus}
              onToggle={toggleSeleccionado}
              onToggleGrupo={toggleGrupo}
              navigate={navigate}
              onClose={onClose}
              onAbrirVistaPrevia={abrirVistaPrevia}
            />
          ))}
        </div>

        {nSeleccionados > 0 && !panelBulk && (
          <div style={{
            flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
            borderTop: `2px solid ${ACCENT}`,
            background: theme.name === "dark" ? "rgba(22,163,74,.14)" : "rgba(22,163,74,.10)",
          }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: ACCENT, flex: 1 }}>
              {nSeleccionados} contacto{nSeleccionados !== 1 ? "s" : ""} seleccionado{nSeleccionados !== 1 ? "s" : ""}
              {waPendientes > 0 && <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}> — verificando WA...</span>}
            </span>
            <button type="button" onClick={() => setPanelBulk("wa")}
              style={{
                padding: "8px 16px", borderRadius: 9, border: "none",
                background: BTN_WA, color: "#000", fontWeight: 800, fontSize: 13,
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 2px 6px rgba(37,211,102,0.35)",
              }}>
              WhatsApp
            </button>
            <button type="button" onClick={() => setPanelBulk("email")}
              style={{
                padding: "8px 16px", borderRadius: 9, border: "none",
                background: BTN_EMAIL, color: "#000", fontWeight: 800, fontSize: 13,
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 2px 6px rgba(249,115,22,0.35)",
              }}>
              Email
            </button>
            {nSeleccionados === 1 && (contactosSeleccionados[0]?.cliente_whatsapp || contactosSeleccionados[0]?.cliente_telefono) && (
              <button type="button" onClick={handleLlamar} disabled={llamando}
                style={{
                  padding: "8px 16px", borderRadius: 9, border: "none",
                  background: BTN_LLAMAR, color: "#000", fontWeight: 800, fontSize: 13,
                  cursor: llamando ? "wait" : "pointer", fontFamily: "inherit",
                  opacity: llamando ? 0.7 : 1,
                  boxShadow: "0 2px 6px rgba(255,222,33,0.4)",
                }}>
                {llamando ? "Llamando..." : "Llamar"}
              </button>
            )}
          </div>
        )}

        {panelBulk && (
          <PanelBulkSend
            canal={panelBulk}
            contactos={contactosSeleccionados}
            propiedadId={propiedadId}
            propiedadLabel={propiedadLabel}
            waStatus={waStatus}
            c={c} theme={theme}
            onClosePanel={() => setPanelBulk(null)}
            onCloseAll={onClose}
          />
        )}

        {vistaPreviaPropId && (
          <FichaResumenPropiedad
            propiedadId={vistaPreviaPropId}
            onClose={() => setVistaPreviaPropId(null)}
          />
        )}
      </div>
    </div>
  );
}
